import { LayoutType } from "@kalyzee/escaet-ui";
import React, { useContext, useState } from "react";
import { StudentView, PopulatedMeetingData, ActivityData } from "../../../interfaces/populatedData";
import { getMediaParam, MediaKeys, setMediaParam } from "../../../helpers/storage";

export interface StudentMeetingOptions {
  enabledVideoSessionIds?: string[];
}

export interface StudentContextState {
  layout: LayoutType;
  setLayout: (type: LayoutType) => void;

  activityData: ActivityData | undefined;
  setActivityData: (data?: ActivityData) => void;
  populatedMeetingData: PopulatedMeetingData | undefined;
  setPopulatedMeetingData: (data?: PopulatedMeetingData) => void;
  availableViews: StudentView[] | undefined;
  setAvailableViews: (views?: StudentView[]) => void;
  currentView: StudentView | undefined;
  setCurrentView: (view?: StudentView) => void;

  inputAudioDeviceInfo: MediaDeviceInfo | undefined;
  setInputAudioDeviceInfo: (device?: MediaDeviceInfo) => void;
  inputVideoDeviceInfo: MediaDeviceInfo | undefined;
  setInputVideoDeviceInfo: (device?: MediaDeviceInfo) => void;
  outputAudioDeviceInfo: MediaDeviceInfo | undefined;
  setOutputAudioDeviceInfo: (device?: MediaDeviceInfo) => void;

  selfVideoStream: MediaStream | undefined;
  setSelfVideoStream: (stream?: MediaStream) => void;

  selfScreenStream: MediaStream | undefined;
  setSelfScreenStream: (stream?: MediaStream) => void;

  audioStreamEnabled: boolean;
  setAudioStreamEnabled: (enabled: boolean) => void;
  videoStreamEnabled: boolean;
  setVideoStreamEnabled: (enabled: boolean) => void;

  getMeetingOptions: (meetingId: string) => StudentMeetingOptions | undefined;
  updateMeetingOptions: (meetingId: string, options: StudentMeetingOptions) => void;
}

const StudentContext = React.createContext<StudentContextState | undefined>(undefined);

export interface StudentContextProviderProps {
  children: React.ReactNode;
}

export const useStudentContext = (): StudentContextState | undefined => useContext(StudentContext);

export const StudentContextProvider = ({ children }: StudentContextProviderProps) => {
  const [layout, setLayout] = useState(LayoutType.Square);

  const [activityData, setActivityData] = useState<ActivityData>();
  const [populatedMeetingData, setPopulatedMeetingData] = useState<PopulatedMeetingData>();
  const [availableViews, setAvailableViews] = useState<StudentView[]>();
  const [currentView, setCurrentView] = useState<StudentView>();

  const [inputAudioDeviceInfo, setInputAudioDeviceInfo] = useState<MediaDeviceInfo>();
  const [inputVideoDeviceInfo, setInputVideoDeviceInfo] = useState<MediaDeviceInfo>();
  const [outputAudioDeviceInfo, setOutputAudioDeviceInfo] = useState<MediaDeviceInfo>();

  const [selfVideoStream, setSelfVideoStream] = useState<MediaStream>();
  const [selfScreenStream, setSelfScreenStream] = useState<MediaStream>();

  const [audioStreamEnabled, setAudioStreamEnabled] = useState(getMediaParam(MediaKeys.INTPUT_AUDIO_ENABLED) ?? false);
  const [videoStreamEnabled, setVideoStreamEnabled] = useState(getMediaParam(MediaKeys.INTPUT_VIDEO_ENABLED) ?? false);

  const [meetingOptions, setMeetingOptions] = useState<{ [meetingId: string]: StudentMeetingOptions }>({});

  const getMeetingOptions = (meetingId: string) => meetingOptions[meetingId];
  
  const updateMeetingOptions = (meetingId: string, update: StudentMeetingOptions) => {
    setMeetingOptions((prev) => {
      const prevOptions = prev[meetingId] ?? {};
      const updatedOptions = { ...prevOptions, ...update };
      return { ...prev, [meetingId]: updatedOptions };
    });
  }

  const state: StudentContextState = {
    layout,
    setLayout,

    activityData,
    setActivityData,
    populatedMeetingData,
    setPopulatedMeetingData,
    availableViews,
    setAvailableViews,
    currentView,
    setCurrentView,

    inputAudioDeviceInfo,
    setInputAudioDeviceInfo,
    inputVideoDeviceInfo,
    setInputVideoDeviceInfo,
    outputAudioDeviceInfo,
    setOutputAudioDeviceInfo,

    selfVideoStream,
    setSelfVideoStream,

    selfScreenStream,
    setSelfScreenStream,

    audioStreamEnabled,
    setAudioStreamEnabled: (enabled) => {
      setMediaParam(MediaKeys.INTPUT_AUDIO_ENABLED, enabled);
      setAudioStreamEnabled(enabled);
    },
    videoStreamEnabled,
    setVideoStreamEnabled: (enabled) => {
      setMediaParam(MediaKeys.INTPUT_VIDEO_ENABLED, enabled);
      setVideoStreamEnabled(enabled);
    },

    getMeetingOptions,
    updateMeetingOptions,
  }

  return (
    <StudentContext.Provider value={state}>
      {children}
    </StudentContext.Provider>
  );
};